/**
 * Estado global do app React sobre o motor legado.
 * - O motor mantém `DB` e `session` (mesmas chaves do localStorage do app antigo:
 *   sulcoia.v1, sulcoia.session, sulcoia.pending...). App novo e /legacy/ leem e gravam o
 *   mesmo estado no mesmo aparelho.
 * - React assina mudanças via useSyncExternalStore; toda mutação passa por `mutate()`,
 *   que grava (save → marca pendência → agenda o envio à nuvem) e notifica.
 */
import { useSyncExternalStore } from "react";
import * as E from "./legacy/engine.gen.js";
import { installHost } from "./legacy/host";
import type { DBState, Session } from "./types";

export type SyncStatus = "off" | "ok" | "salvando" | "offline" | "erro";
export interface Toast { id: number; msg: string; err?: boolean }

let version = 0;
let syncForced: SyncStatus | null = null;
let toasts: Toast[] = [];
let booted = false;
const listeners = new Set<() => void>();

function notify() {
  version++;
  // O motor muta DB no lugar; uma nova referência de topo invalida os useMemo([db]) do React.
  // (O setter de `state.DB` reaponta a variável DB do próprio motor para o mesmo objeto novo.)
  if (E.state.DB) E.state.DB = { ...E.state.DB };
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function toast(msg: string, err?: boolean) {
  const t = { id: Date.now() + Math.random(), msg, err };
  toasts = [...toasts, t];
  notify();
  setTimeout(() => {
    toasts = toasts.filter((x) => x.id !== t.id);
    notify();
  }, 3200);
}

export function boot() {
  if (booted) return;
  booted = true;
  installHost({
    rerender: notify,
    toast: (m: string, err?: boolean) => toast(String(m).replace(/\s*✓$/, ""), err),
    storageChanged: notify,
    authChanged: notify,
    syncChanged: (st?: unknown) => {
      syncForced = typeof st === "string" ? (st as SyncStatus) : null;
      notify();
    },
    syncConflict: () => toast("Os dados mudaram em outro aparelho. Mesclamos automaticamente.", false),
    logError: (...a: unknown[]) => console.warn("[sulco]", ...a),
    navigate: () => notify(),
  });
  E.state.DB = E.load();
  if (E.state.session) {
    try { E.perfilDaConta(); } catch { /* sem metadados */ }
    E.syncPollStart?.();
    E.aberturaInteligente().catch(() => {});
  }
  // outra aba (inclusive o app clássico em /legacy/) gravou: recarrega
  window.addEventListener("storage", (ev) => {
    if (ev.key === E.KEY && ev.newValue) {
      try { E.state.DB = E.normalize(JSON.parse(ev.newValue)); notify(); } catch { /* ignora */ }
    }
    if (ev.key === "sulcoia.session") {
      try { E.state.session = ev.newValue ? JSON.parse(ev.newValue) : null; notify(); } catch { /* ignora */ }
    }
  });
  // O legado dispara cloudPush() sem await em alguns fluxos (ex.: demoSeed); sem conta ou
  // sem sinal isso rejeita — esperado no modo local/offline, não é erro do usuário.
  window.addEventListener("unhandledrejection", (ev) => {
    if (/Entre na sua conta|Sem conexão/i.test(String(ev.reason?.message || ""))) ev.preventDefault();
  });
  window.addEventListener("online", () => { E.dispararSync(); notify(); });
  window.addEventListener("offline", notify);
}

/** Lê o estado e re-renderiza quando ele muda. */
export function useStore() {
  useSyncExternalStore(subscribe, () => version);
  return {
    db: E.state.DB as DBState,
    session: E.state.session as Session | null,
    toasts,
    sync: syncStatus(),
  };
}

export function syncStatus(): SyncStatus {
  if (!E.syncPodeEnviar()) return "off";
  if (syncForced) return syncForced;
  if (typeof navigator !== "undefined" && !navigator.onLine && E.state._pendente) return "offline";
  if (E.state._syncEnviando || E.state._pendente) return "salvando";
  return "ok";
}

/** Aplica uma mudança no estado, grava e agenda a sincronização. */
export function mutate<T>(fn: (db: DBState) => T): T {
  const r = fn(E.state.DB);
  E.save();
  notify();
  return r;
}

export function refresh() { notify(); }
export { E };
